/**
 * Manual del Gerente.
 * Verified behavior (V17+):
 *  - GASTOS: somete recibos como cualquier rol. Al someter su PROPIO recibo en un
 *    solo paso, este omite el Nivel 1 y va directo a Revisión Final
 *    (receiptController createReceipt: manager → pending_review).
 *  - NÓMINA: es el aprobador de Nivel 2, SOLO en proyectos de 3 niveles
 *    (status 'pending_manager', resuelto vía ganga como 'manager'). Aprobar avanza
 *    a Revisión Final (pending_clerk). Devolver regresa al Ingeniero(a).
 *    En proyectos de 2 niveles no existe paso de Gerente.
 * Contenido derivado de: receiptController, nominaController
 * (approveSheet 1966-1979, returnSheet 2134-2137), TarjetaDetailPage.
 */
export default {
  roleKey: 'manager',
  roleLabel: 'Gerente',
  icon: '✅',
  subtitle: 'Nómina — aprobar tarjetas en Nivel 2 (proyectos de 3 niveles). También puede someter gastos.',
  sections: [
    {
      id: 'someter-gasto',
      group: 'Gastos',
      title: 'Cómo someter un gasto',
      blocks: [
        { type: 'paragraph', text: 'Usted puede someter recibos igual que cualquier usuario.' },
        { type: 'steps', items: [
          'Abra "Gastos" → "Mis Recibos" y pulse "+ Nuevo Gasto".',
          'Suba la foto o el PDF; la IA autocompleta proveedor, fecha, subtotal y partidas. Revise que coincidan.',
          'Complete los datos del recibo y, en cada partida, el Proyecto, la Descripción y el Monto.',
          'Pulse "Enviar Reporte".',
        ] },
        { type: 'note', variant: 'warning', text: 'Cuando usted somete su PROPIO recibo, este NO pasa por el Nivel 1 del Ingeniero: va directo a Revisión Final. Por eso no lo verá en ninguna cola de aprobación intermedia.' },
      ],
    },
    {
      id: 'gasto-takeover-backup',
      group: 'Gastos',
      title: 'Cuando el ingeniero no está disponible (respaldo)',
      blocks: [
        { type: 'note', variant: 'warning', text: 'Esta es una función de RESPALDO, no parte del flujo normal. En el flujo normal, los gastos los codifica y aprueba el Ingeniero(a). Use esto solo cuando el ingeniero no pueda revisar.' },
        { type: 'paragraph', text: 'En los proyectos donde usted forma parte del equipo (ganga), si el Ingeniero(a) no está disponible, usted puede tomar su lugar en la revisión de Gastos (Nivel 1).' },
        { type: 'steps', items: [
          'Abra "Gastos" → "Recibos por Aprobar".',
          'Abra un recibo pendiente de un proyecto donde usted está en el equipo.',
          'Codifique las partidas (Tarea / Código GL + Centro de Costo) y pulse "Aprobar Mi Sección", "Devolver Mi Sección" o "Rechazar Mi Sección", igual que lo haría el Ingeniero(a).',
        ] },
        { type: 'note', variant: 'info', text: 'Solo puede hacerlo mientras el recibo está pendiente y solo en proyectos donde usted está en el equipo. El sistema registra que usted actuó en nombre del Ingeniero(a).' },
      ],
    },
    {
      id: 'rol-nomina',
      group: 'Nómina',
      title: 'Su rol en Nómina (solo proyectos de 3 niveles)',
      blocks: [
        { type: 'paragraph', text: 'En Nómina usted es el aprobador de Nivel 2 (L2). Revisa las tarjetas DESPUÉS del Ingeniero(a) y ANTES de Revisión Final.' },
        { type: 'note', variant: 'warning', text: 'Su aprobación solo aplica en proyectos de 3 niveles. En los proyectos de 2 niveles no hay paso de Gerente: la tarjeta pasa del Ingeniero(a) directo a Revisión Final. Si un proyecto no muestra un paso de Gerente, es porque es de 2 niveles — es normal.' },
        { type: 'note', variant: 'info', text: 'El sistema lo reconoce como Gerente del proyecto a través de la ganga (equipo del proyecto). Solo verá tarjetas de los proyectos donde usted es el Gerente asignado.' },
      ],
    },
    {
      id: 'aprobar-l2',
      group: 'Nómina',
      title: 'Revisar y aprobar tarjetas (L2)',
      blocks: [
        { type: 'paragraph', text: 'Su cola contiene las tarjetas en estado "Pendiente Gerente" (ya aprobadas por el Ingeniero) de sus proyectos de 3 niveles.' },
        { type: 'steps', items: [
          'En "Horas", abra una tarjeta "Pendiente Gerente" (se abre en modo revisión).',
          'Revise las horas de cada trabajador (regulares "R" y extra "E") y sus tareas.',
          'Use la barra "AMGtime" para comparar las horas con los ponches reales; pulse "Refrescar" si necesita actualizarlas.',
          'Si todo está correcto, pulse "Aprobar".',
        ] },
        { type: 'note', variant: 'info', text: 'Al aprobar, la tarjeta avanza a Revisión Final (el último nivel).' },
        { type: 'note', variant: 'info', text: 'Indicadores AMGtime: ✓ verde = las horas coinciden con el ponche; ámbar = hay diferencia; "Sin ponche" = el trabajador no tiene registro ese día. Úselos como guía antes de aprobar.' },
      ],
    },
    {
      id: 'devolver-tarjeta',
      group: 'Nómina',
      title: 'Devolver una tarjeta',
      blocks: [
        { type: 'steps', items: [
          'En la tarjeta en revisión, pulse "Devolver".',
          'Escriba la razón de la devolución (es obligatoria) y las notas necesarias.',
          'La tarjeta regresa al Ingeniero(a) para que la corrija y la reenvíe.',
        ] },
        { type: 'note', variant: 'warning', text: 'No puede devolver una tarjeta que usted mismo creó. La razón de devolución es obligatoria.' },
      ],
    },
  ],
};
